import { useState, useRef, useEffect } from "react";

// ── THEME ──────────────────────────────────────────────────────────────────
const C = {
  bg: "#f0f4ff", bg2: "#ffffff", bg3: "#e8eeff", bg4: "#d8e4ff",
  green: "#00b37d", greenDim: "rgba(0,179,125,0.08)", greenBorder: "rgba(0,179,125,0.25)",
  gold: "#e6a800", goldDim: "rgba(230,168,0,0.08)",
  blue: "#1a56db", blueDim: "rgba(26,86,219,0.08)",
  red: "#e02424", redDim: "rgba(224,36,36,0.08)",
  orange: "#ff7700",
  text: "#0f172a", text2: "#374151", text3: "#9ca3af", border: "#e2e8f0",
};

const gStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${C.bg};font-family:'DM Sans',sans-serif;color:${C.text};}
  ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:#c7d2fe;border-radius:3px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes shimmerBg{0%{background-position:0% 50%}100%{background-position:100% 50%}}
  .fu{animation:fadeUp 0.35s ease forwards;}
  input,select,textarea{font-family:'DM Sans',sans-serif;color:#0f172a;}
  input::placeholder{color:#9ca3af;}
  select option{background:#fff;color:#0f172a;}
`;

// ── DSF EVALUATION FORMULAS ────────────────────────────────────────────────
const BMI_THRESHOLDS = {
  Boy:  {9:[14,18.6,21],10:[14.2,19.4,22],11:[14.6,20.2,23.2],12:[15,21,24.2],13:[15.4,21.8,25.2],14:[16,22.6,26],15:[16.6,23.4,26.8],16:[17.2,24.2,27.6],17:[17.8,25,28.2],18:[18.4,25.6,29]},
  Girl: {9:[13.8,19,21.6],10:[14,19.8,23],11:[14.4,20.8,24],12:[14.8,21.8,25],13:[15.2,22.6,26.2],14:[15.8,23.4,27.2],15:[16.2,24,28],16:[16.8,24.6,28.8],17:[17.2,25.2,29.6],18:[17.6,25.6,30.1]},
};
function evalBMI(gender, age, bmi) {
  if (!bmi || bmi === 0) return "";
  const t = BMI_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (bmi <= t[0]) return "Underweight";
  if (bmi < t[1]) return "Normal";
  if (bmi < t[2]) return "Overweight";
  return "Obese";
}

const ABS_THRESHOLDS = {
  Boy:  {9:[13,13,18,20,21,23],10:[15,15,20,22,23,25],11:[16,16,22,23,24,26],12:[16,16,22,24,25,27],13:[18,18,23,24,26,28],14:[19,19,24,25,27,29],15:[20,20,25,26,28,30],16:[20,20,26,27,29,30],17:[22,22,27,28,29,31],18:[21,21,28,29,30,31]},
  Girl: {9:[13,13,18,19,21,23],10:[14,14,19,20,22,24],11:[15,15,20,21,22,24],12:[15,15,20,21,23,24],13:[15,15,20,21,23,24],14:[16,16,20,21,23,24],15:[16,16,21,22,23,25],16:[16,16,21,22,23,25],17:[17,17,21,22,23,25],18:[16,16,21,22,24,25]},
};
function evalAbs(gender, age, val) {
  if (!val || val === 0) return "";
  const t = ABS_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (val < t[0]) return "L1 (Work Harder)";
  if (val === t[1]) return "L2 (Most Improved)";
  if (val <= t[2]) return "L3 (Can do better)";
  if (val <= t[3]) return "L4 (Good)";
  if (val <= t[4]) return "L5 (Very Good)";
  if (val <= t[5]) return "L6 (Athletic)";
  return "L7 (Sports Fit)";
}

const MUSC_THRESHOLDS = {
  Boy:  {9:[5,6,7,8,9,10],10:[6,7,8,9,10,11],11:[7,8,9,10,11,13],12:[8,9,10,11,13,15],13:[9,10,11,13,15,16],14:[10,11,13,15,16,17],15:[15,17,19,21,23,28],16:[17,19,21,23,28,33],17:[19,21,23,28,33,37],18:[21,23,28,33,37,43]},
  Girl: {9:[4,6,7,7,8,8],10:[6,7,7,8,8,9],11:[7,7,8,8,9,10],12:[7,8,8,9,10,12],13:[8,8,9,10,12,14],14:[11,13,15,16,17,19],15:[13,15,16,17,19,21],16:[15,16,17,19,21,22],17:[16,17,19,21,22,24],18:[17,19,21,22,24,27]},
};
function evalMusc(gender, age, val) {
  if (!val || val === 0) return "";
  const t = MUSC_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (val <= t[0]) return "L1 (Work Harder)";
  if (val <= t[1]) return "L2 (Most Improved)";
  if (val <= t[2]) return "L3 (Can do better)";
  if (val <= t[3]) return "L4 (Good)";
  if (val <= t[4]) return "L5 (Very Good)";
  if (val <= t[5]) return "L6 (Athletic)";
  return "L7 (Sports Fit)";
}

const FLEX_THRESHOLDS = {
  Boy:  {9:[10.7,14.4,17.6,19.4,21.4,26.8],10:[10.8,14.4,17.7,19.4,21.5,26.9],11:[10.8,14.4,17.7,19.4,21.5,26.9],12:[10.8,14.5,17.8,19.6,21.7,27.1],13:[11.1,14.8,18.2,20,22.2,27.7],14:[12.1,16.2,19.9,21.9,24.2,30.3],15:[13.7,18.3,22.4,24.6,27.2,34],16:[14.6,19.3,23.6,25.9,28.6,35.6],17:[15.5,20.4,24.8,27.2,30,37.2],18:[15.5,19.3,24.8,27.3,30.2,37.4]},
  Girl: {9:[13.1,16.9,20.3,22.1,24.2,29.6],10:[13.7,17.5,20.9,22.7,24.8,30.1],11:[14.5,18.4,21.7,23.5,25.6,31],12:[15.8,19.7,23.1,24.9,27.1,32.5],13:[17.3,21.3,24.8,26.7,28.9,34.4],14:[18.6,22.7,26.3,28.2,30.4,36.1],15:[19.5,23.6,27.2,29.1,31.3,37],16:[20,24.1,27.6,29.5,31.8,37.5],17:[20.3,24.4,27.9,29.8,32.1,37.8],18:[20.7,24.7,28.2,30,32.3,38]},
};
function evalFlex(gender, age, val) {
  if (!val || val === 0) return "";
  const t = FLEX_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (val <= t[0]) return "L1 (Work Harder)";
  if (val <= t[1]) return "L2 (Most Improved)";
  if (val <= t[2]) return "L3 (Can do better)";
  if (val <= t[3]) return "L4 (Good)";
  if (val <= t[4]) return "L5 (Very Good)";
  if (val <= t[5]) return "L6 (Athletic)";
  return "L7 (Sports Fit)";
}

// Cardio: time in seconds
const CARDIO_THRESHOLDS = {
  Boy:  {9:[204,200,197,196,195,195],10:[208,202,197,195,192,192],11:[202,196,191,189,186,186],12:[186,172,163,159,151,151],13:[134,128,124,123,120,120],14:[128,93,91,90,88,70],15:[94,93,91,90,89,88],16:[94,92,91,90,89,88],17:[93,91,90,89,88,87],18:[91,89,87,85,84,83]},
  Girl: {9:[246,234,225,221,216,215],10:[237,224,214,210,204,204],11:[231,218,208,204,198,198],12:[204,194,187,184,180,180],13:[169,154,144,139,132,132],14:[134,128,124,123,122,121],15:[128,93,91,90,89,88],16:[94,93,91,90,89,88],17:[94,92,91,90,89,88],18:[93,91,90,89,88,87]},
};
function evalCardio(gender, age, secs) {
  if (!secs || secs === 0) return "";
  const t = CARDIO_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (secs >= t[0]) return "L1 (Work Harder)";
  if (secs >= t[1]) return "L2 (Most Improved)";
  if (secs >= t[2]) return "L3 (Can do better)";
  if (secs >= t[3]) return "L4 (Good)";
  if (secs > t[4]) return "L5 (Very Good)";
  if (secs === t[5]) return "L6 (Athletic)";
  return "L7 (Sports Fit)";
}

const SPEED_THRESHOLDS = {
  Boy:  {9:[10.3,9.8,9.3,9.1,9,8.6],10:[10,9.5,9.1,8.9,8.3,8.3],11:[9.7,9.2,8.8,8.6,8.1,8.1],12:[9.4,8.9,8.5,8.3,7.8,7.8],13:[9.1,8.7,8.3,8,7.7,7.7],14:[8.9,8.5,8,7.8,7.5,7.5],15:[8.7,8.3,7.8,7.7,7.3,7.3],16:[8.5,8,7.7,7.6,7.2,7.2],17:[8.3,7.8,7.6,7.5,7.1,7.1],18:[8,7.7,7.5,7.3,7,7]},
  Girl: {9:[10.6,9.9,9.5,9.3,9.1,8.8],10:[10.2,9.7,9.3,9.1,8.8,8.5],11:[9.5,9,8.6,8.5,8.4,8.3],12:[9.5,9,8.6,8.4,8.2,8],13:[9.2,8.8,8.4,8.1,8,7.8],14:[9,8.6,8.1,7.9,7.8,7.6],15:[8.8,8.4,7.9,7.6,7.6,7.4],16:[8.6,8.1,7.6,7.4,7.4,7.2],17:[8.4,7.9,7.4,7.3,7.3,7],18:[7.8,7.6,7.3,7.1,7.1,6.8]},
};
function evalSpeed(gender, age, val) {
  if (!val || val === 0) return "";
  const t = SPEED_THRESHOLDS[gender]?.[age];
  if (!t) return "";
  if (val >= t[0]) return "L1 (Work Harder)";
  if (val >= t[1]) return "L2 (Most Improved)";
  if (val >= t[2]) return "L3 (Can do better)";
  if (val >= t[3]) return "L4 (Good)";
  if (val >= t[4]) return "L5 (Very Good)";
  if (val >= t[5]) return "L6 (Athletic)";
  return "L7 (Sports Fit)";
}

const LEVEL_COLOR = {
  "L1 (Work Harder)": C.red, "L2 (Most Improved)": C.orange,
  "L3 (Can do better)": C.gold, "L4 (Good)": "#a8d8a8",
  "L5 (Very Good)": C.green, "L6 (Athletic)": C.blue,
  "L7 (Sports Fit)": "#bf7fff",
  "Underweight": C.blue, "Normal": C.green, "Overweight": C.gold, "Obese": C.red,
};
const LEVEL_NUM = { "L1 (Work Harder)": 1, "L2 (Most Improved)": 2, "L3 (Can do better)": 3, "L4 (Good)": 4, "L5 (Very Good)": 5, "L6 (Athletic)": 6, "L7 (Sports Fit)": 7 };

function getWeakAreas(result) {
  const areas = [];
  const checks = [
    { key: "abs", label: "Abdominal Strength", val: result.abs },
    { key: "musc", label: "Muscular Endurance", val: result.musc },
    { key: "flex", label: "Flexibility", val: result.flex },
    { key: "cardio", label: "Cardiovascular Endurance", val: result.cardio },
    { key: "speed", label: "Speed", val: result.speed },
  ];
  for (const c of checks) {
    const n = LEVEL_NUM[c.val] || 0;
    if (c.val && n <= 3) areas.push(c.label);
  }
  if (result.bmi === "Underweight" || result.bmi === "Overweight" || result.bmi === "Obese") areas.push("BMI/Body Composition");
  return areas;
}

function evaluateStudent(s) {
  const age = parseInt(s.age);
  const bmiVal = s.weight && s.height ? parseFloat((s.weight / ((s.height / 100) ** 2)).toFixed(1)) : 0;
  const cardioSecs = s.cardioMin && s.cardioSec ? parseInt(s.cardioMin) * 60 + parseInt(s.cardioSec) : 0;
  return {
    name: s.name, gender: s.gender, age,
    bmiVal, bmi: evalBMI(s.gender, age, bmiVal),
    abs: evalAbs(s.gender, age, parseFloat(s.abs)),
    musc: evalMusc(s.gender, age, parseFloat(s.musc)),
    flex: evalFlex(s.gender, age, parseFloat(s.flex)),
    cardio: evalCardio(s.gender, age, cardioSecs),
    speed: evalSpeed(s.gender, age, parseFloat(s.speed)),
    cardioSecs,
  };
}

// ── COMPONENTS ────────────────────────────────────────────────────────────
const inputSx = { background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "10px 14px", fontSize: 14, width: "100%", outline: "none" };
const labelSx = { color: C.text3, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 5, letterSpacing: "0.06em", textTransform: "uppercase" };

function Badge({ val }) {
  if (!val) return <span style={{ color: C.text3, fontSize: 12 }}>—</span>;
  return <span style={{ background: (LEVEL_COLOR[val] || C.text2) + "18", color: LEVEL_COLOR[val] || C.text2, border: `1px solid ${(LEVEL_COLOR[val] || C.text2)}30`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{val}</span>;
}

function Loader({ msg = "AI soch raha hai..." }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 0" }}>
      {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, animation: `pulse 1.2s ease ${i*0.2}s infinite` }} />)}
      <span style={{ color: C.text3, fontSize: 13, marginLeft: 4 }}>{msg}</span>
    </div>
  );
}

const BLANK_STUDENT = { name: "", gender: "Boy", age: "12", height: "", weight: "", abs: "", musc: "", flex: "", cardioMin: "", cardioSec: "", speed: "" };

// ── SINGLE INPUT ──────────────────────────────────────────────────────────
function SingleInput({ onResult }) {
  const [form, setForm] = useState({ ...BLANK_STUDENT });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const evaluate = () => {
    if (!form.name || !form.age) return;
    onResult(evaluateStudent(form));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelSx}>Student Name</label>
          <input type="text" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Rahul Kumar" style={inputSx} />
        </div>
        <div>
          <label style={labelSx}>Gender</label>
          <select value={form.gender} onChange={e => set("gender", e.target.value)} style={inputSx}>
            <option>Boy</option><option>Girl</option>
          </select>
        </div>
        <div>
          <label style={labelSx}>Age (9–18)</label>
          <select value={form.age} onChange={e => set("age", e.target.value)} style={inputSx}>
            {[...Array(10)].map((_,i) => <option key={i+9}>{i+9}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelSx}>Height (cm)</label>
          <input type="number" value={form.height} onChange={e => set("height", e.target.value)} placeholder="150" style={inputSx} />
        </div>
        <div>
          <label style={labelSx}>Weight (kg)</label>
          <input type="number" value={form.weight} onChange={e => set("weight", e.target.value)} placeholder="45" style={inputSx} />
        </div>
      </div>

      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
        <div style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 12, textTransform: "uppercase" }}>Skill Measurements</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div><label style={labelSx}>Abdominal Strength (reps)</label><input type="number" value={form.abs} onChange={e => set("abs", e.target.value)} placeholder="20" style={inputSx} /></div>
          <div><label style={labelSx}>Muscular Endurance (reps)</label><input type="number" value={form.musc} onChange={e => set("musc", e.target.value)} placeholder="12" style={inputSx} /></div>
          <div><label style={labelSx}>Flexibility (cm)</label><input type="number" value={form.flex} onChange={e => set("flex", e.target.value)} placeholder="22" style={inputSx} /></div>
          <div><label style={labelSx}>Speed - 50m (seconds)</label><input type="number" value={form.speed} onChange={e => set("speed", e.target.value)} placeholder="8.5" style={inputSx} /></div>
          <div><label style={labelSx}>Cardio - Min</label><input type="number" value={form.cardioMin} onChange={e => set("cardioMin", e.target.value)} placeholder="3" style={inputSx} /></div>
          <div><label style={labelSx}>Cardio - Sec</label><input type="number" value={form.cardioSec} onChange={e => set("cardioSec", e.target.value)} placeholder="30" style={inputSx} /></div>
        </div>
      </div>

      <button onClick={evaluate} style={{ background: `linear-gradient(135deg, ${C.green}, #00b37d)`, border: "none", borderRadius: 10, padding: "13px", color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
        ⚡ Evaluate Student
      </button>
    </div>
  );
}

// ── BULK INPUT ─────────────────────────────────────────────────────────────
function BulkInput({ onResults }) {
  const [csv, setCsv] = useState("");
  const [error, setError] = useState("");

  const process = () => {
    setError("");
    const lines = csv.trim().split("\n").filter(Boolean);
    const results = [];
    for (const line of lines) {
      const p = line.split(",").map(x => x.trim());
      if (p.length < 10) { setError(`Invalid row: ${line.substring(0,40)}...`); return; }
      const [name, gender, age, height, weight, abs_, musc, flex, speed, cardioMin, cardioSec] = p;
      results.push(evaluateStudent({ name, gender, age, height, weight, abs: abs_, musc, flex, speed, cardioMin: cardioMin||"0", cardioSec: cardioSec||"0" }));
    }
    onResults(results);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
        <div style={{ color: C.text2, fontSize: 13, marginBottom: 8 }}>
          Format: <span style={{ fontFamily: "monospace", color: C.green, fontSize: 12 }}>Name, Gender(Boy/Girl), Age, Height(cm), Weight(kg), Abdominal(reps), Muscular(reps), Flexibility(cm), Speed(sec), CardioMin, CardioSec</span>
        </div>
        <div style={{ color: C.text3, fontSize: 12, marginBottom: 12 }}>Example: Rahul Kumar, Boy, 13, 158, 48, 20, 12, 22.5, 8.3, 2, 15</div>
        <textarea value={csv} onChange={e => setCsv(e.target.value)}
          placeholder={"Rahul Kumar, Boy, 13, 158, 48, 20, 12, 22.5, 8.3, 2, 15\nPriya Sharma, Girl, 12, 152, 42, 18, 9, 25, 9.1, 3, 10"}
          style={{ ...inputSx, minHeight: 140, resize: "vertical", lineHeight: 1.6 }} />
        {error && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{error}</div>}
      </div>
      <button onClick={process} disabled={!csv.trim()}
        style={{ background: csv.trim() ? `linear-gradient(135deg, ${C.gold}, #c49a00)` : C.border, border: "none", borderRadius: 10, padding: "13px", color: csv.trim() ? "#000" : C.text3, fontSize: 15, fontWeight: 700, cursor: csv.trim() ? "pointer" : "not-allowed" }}>
        📊 Process Bulk Data
      </button>
    </div>
  );
}

// ── RESULT CARD ────────────────────────────────────────────────────────────
function ResultCard({ result, onPlanRequest }) {
  const weak = getWeakAreas(result);
  const skills = [
    { label: "BMI", val: result.bmi, extra: result.bmiVal ? `${result.bmiVal}` : "" },
    { label: "Abdominal Strength", val: result.abs },
    { label: "Muscular Endurance", val: result.musc },
    { label: "Flexibility", val: result.flex },
    { label: "Cardiovascular", val: result.cardio },
    { label: "Speed (50m)", val: result.speed },
  ];

  return (
    <div className="fu" style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{result.name}</div>
          <div style={{ color: C.text3, fontSize: 12 }}>{result.gender} • Age {result.age}</div>
        </div>
        {weak.length > 0 && (
          <button onClick={() => onPlanRequest(result)}
            style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, color: C.green, borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            📋 Generate Lesson Plan
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
        {skills.map(s => (
          <div key={s.label} style={{ background: C.bg3, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ color: C.text3, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{s.label}</div>
            <Badge val={s.val} />
            {s.extra && <div style={{ color: C.text2, fontSize: 11, marginTop: 3 }}>BMI: {s.extra}</div>}
          </div>
        ))}
      </div>
      {weak.length > 0 && (
        <div style={{ background: C.redDim, border: `1px solid ${C.red}20`, borderRadius: 8, padding: "10px 14px" }}>
          <span style={{ color: C.red, fontSize: 12, fontWeight: 600 }}>⚠ Needs Attention: </span>
          <span style={{ color: C.text2, fontSize: 12 }}>{weak.join(", ")}</span>
        </div>
      )}
      {weak.length === 0 && (
        <div style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: "10px 14px" }}>
          <span style={{ color: C.green, fontSize: 12, fontWeight: 600 }}>✓ Great overall performance! Keep it up.</span>
        </div>
      )}
    </div>
  );
}

// ── LESSON PLAN ────────────────────────────────────────────────────────────
function LessonPlanPanel({ student, weak, onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState("English");

  useEffect(() => { generate(); }, []);

  const generate = async () => {
    setLoading(true); setPlan(null);
    const langInstr = lang === "Hindi" ? "Respond entirely in Hindi (Devanagari script)." : "Respond in English.";
    const prompt = `You are an expert sports science coach and PE teacher at an Indian school.

${langInstr}

Student Profile:
- Name: ${student.name}
- Gender: ${student.gender}
- Age: ${student.age} years
- BMI Status: ${student.bmi || "Not measured"}
- Abdominal Strength: ${student.abs || "Not measured"}
- Muscular Endurance: ${student.musc || "Not measured"}
- Flexibility: ${student.flex || "Not measured"}
- Cardiovascular: ${student.cardio || "Not measured"}
- Speed: ${student.speed || "Not measured"}

Areas needing improvement: ${weak.join(", ")}

Create a practical 45-minute customized lesson plan specifically addressing the weak areas. Include:
1. Session Goal (1-2 lines specific to this student)
2. Warm-Up (8 min) - 2 activities targeting weak areas
3. Main Drills (25 min) - 3-4 specific exercises for improvement areas with sets/reps
4. Cool-Down (7 min) - stretching focusing on weak areas
5. Weekly Progression Plan - what to do over next 4 weeks
6. One motivational note for the student

Be specific, practical, and use equipment available in Indian schools.`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { 
          "Content-Type": "application/json",
          "x-api-key": "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",
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({ model: "claude-sonnet-4-5", max_tokens: 1000, messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      setPlan(data.content?.find(b => b.type === "text")?.text || "");
    } catch { setPlan("Could not generate. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: C.bg2, border: `1px solid ${C.greenBorder}`, borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>📋 Lesson Plan — {student.name}</div>
            <div style={{ color: C.text3, fontSize: 12, marginTop: 2 }}>Focus: {weak.slice(0,3).join(", ")}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => { setLang(l => l === "English" ? "Hindi" : "English"); setTimeout(generate, 50); }}
              style={{ background: C.bg3, border: `1px solid ${C.border}`, color: C.text2, borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
              {lang === "English" ? "🇮🇳 Hindi mein" : "🇬🇧 English"}
            </button>
            <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, color: C.text2, borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>✕ Close</button>
          </div>
        </div>
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {loading && <Loader msg="Customized plan ban raha hai..." />}
          {plan && <div style={{ color: C.text, fontSize: 14, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{plan}</div>}
        </div>
      </div>
    </div>
  );
}

// ── CHATBOT ────────────────────────────────────────────────────────────────
function Chatbot({ results }) {
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Namaste! 🙏 Main Sportzai ka AI Coach Assistant hun. Aap mujhse students ke results ke baare mein, lesson plans ke baare mein, ya koi bhi sports/fitness query pooch sakte hain — Hindi ya English mein!" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMsgs(p => [...p, { role: "user", text: userMsg }]);
    setLoading(true);

    const context = results.length > 0
      ? `Current session has ${results.length} evaluated student(s): ${results.map(r => `${r.name} (Age ${r.age}, ${r.gender}): BMI=${r.bmi}, Abs=${r.abs}, Musc=${r.musc}, Flex=${r.flex}, Cardio=${r.cardio}, Speed=${r.speed}`).join("; ")}`
      : "No students evaluated yet in this session.";

    const history = msgs.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.text }));

    const systemPrompt = `You are Sportzai's AI Coach Assistant — an expert in Indian school sports, physical education, and student fitness assessment. You help PE teachers and coaches understand student performance data, create improvement plans, and answer sports science questions.

Context about current session: ${context}

DSF Assessment Levels: L1=Work Harder, L2=Most Improved, L3=Can do better, L4=Good, L5=Very Good, L6=Athletic, L7=Sports Fit
BMI Categories: Underweight, Normal, Overweight, Obese

Be helpful, practical, and concise. Support both Hindi and English. If asked about specific students in the session, use the context data.`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { 
          "Content-Type": "application/json",
          "x-api-key": "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",
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-5", max_tokens: 1000,
          system: systemPrompt,
          messages: [...history, { role: "user", content: userMsg }]
        })
      });
      const data = await res.json();
      const reply = data.content?.find(b => b.type === "text")?.text || "Sorry, kuch gadbad hui.";
      setMsgs(p => [...p, { role: "assistant", text: reply }]);
    } catch { setMsgs(p => [...p, { role: "assistant", text: "Network error. Please try again." }]); }
    setLoading(false);
  };

  const suggestions = ["Sabse weak student kaun hai?", "Flexibility improve karne ke drills batao", "BMI normal range kya hai?", "Speed training tips do"];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: 520 }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "80%", background: m.role === "user" ? C.green : C.bg3,
              color: m.role === "user" ? "#000" : C.text,
              borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              padding: "12px 16px", fontSize: 14, lineHeight: 1.65,
            }}>{m.text}</div>
          </div>
        ))}
        {loading && <div style={{ display: "flex", justifyContent: "flex-start" }}><div style={{ background: C.bg3, borderRadius: "16px 16px 16px 4px", padding: "12px 16px" }}><Loader /></div></div>}
        <div ref={bottomRef} />
      </div>

      {msgs.length === 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {suggestions.map(s => (
            <button key={s} onClick={() => setInput(s)}
              style={{ background: C.bg3, border: `1px solid ${C.border}`, color: C.text2, borderRadius: 20, padding: "5px 14px", fontSize: 12, cursor: "pointer" }}>{s}</button>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Koi bhi query poochein — Hindi ya English mein..."
          style={{ ...inputSx, flex: 1 }} />
        <button onClick={send} disabled={!input.trim() || loading}
          style={{ background: input.trim() ? C.green : C.border, border: "none", borderRadius: 8, padding: "0 18px", color: input.trim() ? "#000" : C.text3, fontWeight: 700, cursor: input.trim() ? "pointer" : "not-allowed", fontSize: 18 }}>➤</button>
      </div>
    </div>
  );
}

// ── BULK RESULTS TABLE ─────────────────────────────────────────────────────
function BulkTable({ results, onPlanRequest }) {
  const cols = ["Name", "Gender", "Age", "BMI", "Abdominal", "Muscular", "Flexibility", "Cardio", "Speed", "Action"];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>{cols.map(c => <th key={c} style={{ padding: "10px 12px", textAlign: "left", color: C.text3, fontWeight: 600, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {results.map((r, i) => {
            const weak = getWeakAreas(r);
            return (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : C.bg2 }}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{r.name}</td>
                <td style={{ padding: "10px 12px", color: C.text2 }}>{r.gender}</td>
                <td style={{ padding: "10px 12px", color: C.text2 }}>{r.age}</td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.bmi} /></td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.abs} /></td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.musc} /></td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.flex} /></td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.cardio} /></td>
                <td style={{ padding: "10px 12px" }}><Badge val={r.speed} /></td>
                <td style={{ padding: "10px 12px" }}>
                  {weak.length > 0 && <button onClick={() => onPlanRequest(r)} style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, color: C.green, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>📋 Plan</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── APP ────────────────────────────────────────────────────────────────────
export default function CoachAssistant() {
  const [tab, setTab] = useState("single");
  const [results, setResults] = useState([]);
  const [planFor, setPlanFor] = useState(null);

  const TABS = [
    { id: "single", label: "Single Student", emoji: "👤" },
    { id: "bulk", label: "Bulk Upload", emoji: "📊" },
    { id: "chat", label: "AI Coach Chat", emoji: "🤖" },
  ];

  return (
    <>
      <style>{gStyle}</style>
      <div style={{ minHeight: "100vh", background: C.bg }}>
        {/* HEADER */}
        <div style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1a56db 50%, #0ea5e9 100%)", borderBottom: "none", padding: "14px 28px", boxShadow: "0 4px 20px rgba(26,86,219,0.2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative", width: 42, height: 42, flexShrink: 0 }}>
              <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1a56db"/>
                    <stop offset="100%" stopColor="#00b37d"/>
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                    <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                </defs>
                <rect width="42" height="42" rx="10" fill="url(#logoGrad)"/>
                <polygon points="21,6 36,15 36,27 21,36 6,27 6,15" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
                <text x="21" y="27" textAnchor="middle" fontFamily="Space Mono" fontWeight="700" fontSize="13" fill="white" filter="url(#glow)">S∞</text>
                <circle cx="21" cy="21" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" strokeDasharray="3 3"/>
              </svg>
            </div>
            <div>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 17, letterSpacing: "-0.02em" }}><span style={{ color: "#ffffff" }}>Sportz</span><span style={{ color: "#FF6B00", textShadow: "0 0 12px rgba(255,107,0,0.8)" }}>AI</span></div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>CoachAssistant</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {results.length > 0 && <span style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, color: C.green, borderRadius: 100, padding: "3px 12px", fontSize: 12, fontWeight: 600 }}>{results.length} student{results.length > 1 ? "s" : ""} evaluated</span>}
          </div>
        </div>

        <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 20px" }}>
          {/* TAB NAV */}
          <div style={{ display: "inline-flex", background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, gap: 4, marginBottom: 24 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ background: tab === t.id ? C.greenDim : "transparent", border: `1px solid ${tab === t.id ? C.greenBorder : "transparent"}`, borderRadius: 8, padding: "8px 18px", color: tab === t.id ? C.green : C.text2, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s" }}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {/* LEFT PANEL */}
            <div style={{ flex: "1 1 380px" }}>
              <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
                {tab === "single" && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Student Assessment</div>
                    <div style={{ color: C.text3, fontSize: 13, marginBottom: 20 }}>DSF evaluation formulas — Age 9 to 18</div>
                    <SingleInput onResult={r => setResults(p => [r, ...p.filter(x => x.name !== r.name)])} />
                  </>
                )}
                {tab === "bulk" && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Bulk Student Upload</div>
                    <div style={{ color: C.text3, fontSize: 13, marginBottom: 20 }}>CSV format — multiple students at once</div>
                    <BulkInput onResults={r => setResults(p => [...r, ...p])} />
                  </>
                )}
                {tab === "chat" && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>AI Coach Chat</div>
                    <div style={{ color: C.text3, fontSize: 13, marginBottom: 16 }}>Student data aware • Hindi + English</div>
                    <Chatbot results={results} />
                  </>
                )}
              </div>
            </div>

            {/* RIGHT PANEL — RESULTS */}
            <div style={{ flex: "1 1 340px" }}>
              {results.length === 0 ? (
                <div style={{ background: C.bg2, border: `1px dashed ${C.border}`, borderRadius: 16, padding: 40, textAlign: "center", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <div style={{ fontSize: 40 }}>📊</div>
                  <div style={{ color: C.text2, fontWeight: 600 }}>Results yahan dikhenge</div>
                  <div style={{ color: C.text3, fontSize: 13 }}>Student data daalo aur evaluate karo</div>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>Results ({results.length})</div>
                    <button onClick={() => setResults([])} style={{ background: "none", border: `1px solid ${C.border}`, color: C.text3, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Clear All</button>
                  </div>
                  {results.length > 3 ? (
                    <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
                      <BulkTable results={results} onPlanRequest={r => setPlanFor(r)} />
                    </div>
                  ) : (
                    results.map((r, i) => <ResultCard key={i} result={r} onPlanRequest={r => setPlanFor(r)} />)
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* LESSON PLAN MODAL */}
      {planFor && (
        <LessonPlanPanel student={planFor} weak={getWeakAreas(planFor)} onClose={() => setPlanFor(null)} />
      )}
    </>
  );
}
